/**
 * AudioRecorder component
 *
 * Mobile-first audio recording UI for the Sales Copilot.
 * - Big record button (tap to start, tap to stop)
 * - Pulsing red dot + elapsed time counter while recording
 * - Audio waveform visualization during recording
 * - Returns audio blob on stop via onRecordingComplete callback
 * - Falls back to null if MediaRecorder is unavailable
 */
import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { useAudioRecording, type AudioRecordingResult } from '../hooks/useAudioRecording.ts';
import styles from './AudioRecorder.module.css';

export interface AudioRecorderProps {
  /** Called when a recording completes with the audio blob + metadata */
  onRecordingComplete: (result: AudioRecordingResult) => void;
  /** Optional label override for the record button */
  recordLabel?: string;
  /** Optional content to render in the unsupported state instead of returning null */
  fallback?: ReactNode;
  /** Max recording duration in seconds (default: 600 = 10 min) */
  maxDuration?: number;
  /** Compact layout — smaller mic + tighter spacing for embedded desktop use
   *  (e.g. the Quality Co-Pilot composer). Default false keeps the big
   *  mobile-first tap target the Sales Copilot relies on. */
  compact?: boolean;
}

/** Format seconds into MM:SS display */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/** Number of bars in the waveform visualization */
const WAVEFORM_BARS = 24;

export function AudioRecorder({
  onRecordingComplete,
  recordLabel,
  fallback = null,
  maxDuration = 600,
  compact = false,
}: AudioRecorderProps) {
  const {
    state,
    isRecording,
    duration,
    audioBlob,
    mimeType,
    startRecording,
    stopRecording,
    reset,
    error,
    isSupported,
    analyserNode,
  } = useAudioRecording();

  const [waveformData, setWaveformData] = useState<number[]>(
    () => new Array(WAVEFORM_BARS).fill(0) as number[]
  );

  const animationFrameRef = useRef<number | null>(null);
  const onRecordingCompleteRef = useRef(onRecordingComplete);
  onRecordingCompleteRef.current = onRecordingComplete;

  // Auto-stop at max duration
  useEffect(() => {
    if (isRecording && duration >= maxDuration) {
      stopRecording();
    }
  }, [isRecording, duration, maxDuration, stopRecording]);

  // Deliver recording result when stopped
  useEffect(() => {
    if (state === 'stopped' && audioBlob && mimeType) {
      onRecordingCompleteRef.current({
        blob: audioBlob,
        mimeType,
        duration,
      });
    }
  }, [state, audioBlob, mimeType, duration]);

  // Waveform animation loop
  const updateWaveform = useCallback(() => {
    if (!analyserNode) return;

    const dataArray = new Uint8Array(analyserNode.frequencyBinCount);
    analyserNode.getByteFrequencyData(dataArray);

    // Sample evenly across the frequency range
    const step = Math.floor(dataArray.length / WAVEFORM_BARS);
    const bars: number[] = [];
    for (let i = 0; i < WAVEFORM_BARS; i++) {
      // Normalize 0-255 to 0-1
      const value = dataArray[i * step] / 255;
      bars.push(value);
    }
    setWaveformData(bars);

    animationFrameRef.current = requestAnimationFrame(updateWaveform);
  }, [analyserNode]);

  useEffect(() => {
    if (isRecording && analyserNode) {
      animationFrameRef.current = requestAnimationFrame(updateWaveform);
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isRecording, analyserNode, updateWaveform]);

  // Reset waveform when not recording
  useEffect(() => {
    if (!isRecording) {
      setWaveformData(new Array(WAVEFORM_BARS).fill(0) as number[]);
    }
  }, [isRecording]);

  // If MediaRecorder is not supported, render fallback
  if (!isSupported) {
    return <>{fallback}</>;
  }

  const handleToggle = () => {
    if (isRecording) {
      stopRecording();
    } else {
      if (state === 'stopped') {
        reset();
      }
      startRecording();
    }
  };

  const isRequesting = state === 'requesting_permission';
  const isStopped = state === 'stopped';

  return (
    <div className={`${styles.container}${compact ? ` ${styles.compact}` : ''}`}>
      {/* Waveform visualization */}
      {isRecording && (
        <div className={styles.waveform} aria-hidden="true">
          {waveformData.map((value, index) => (
            <div
              key={index}
              className={styles.waveformBar}
              style={{
                height: `${Math.max(8, value * 100)}%`,
              }}
            />
          ))}
        </div>
      )}

      {/* Recording status indicator */}
      {isRecording && (
        <div className={styles.statusRow}>
          <span className={styles.recordingDot} />
          <span className={styles.timer}>{formatTime(duration)}</span>
          {maxDuration < Infinity && (
            <span className={styles.maxTime}>/ {formatTime(maxDuration)}</span>
          )}
        </div>
      )}

      {/* Stopped state - show duration of last recording */}
      {isStopped && (
        <div className={styles.statusRow}>
          <span className={styles.doneLabel}>Recorded</span>
          <span className={styles.timer}>{formatTime(duration)}</span>
        </div>
      )}

      {/* Main record button */}
      <button
        className={[
          styles.recordButton,
          isRecording ? styles.recordButtonActive : '',
          isRequesting ? styles.recordButtonRequesting : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={handleToggle}
        disabled={isRequesting}
        aria-label={isRecording ? 'Stop recording' : 'Start recording'}
        type="button"
      >
        <span className={styles.recordButtonInner}>
          {isRecording ? (
            <span className={styles.stopIcon} />
          ) : (
            <span className={styles.micIcon} />
          )}
        </span>
      </button>

      {/* Button label */}
      <span className={styles.buttonLabel}>
        {isRequesting
          ? 'Requesting mic access...'
          : isRecording
            ? 'Tap to stop'
            : isStopped
              ? (recordLabel ?? 'Record again')
              : (recordLabel ?? 'Tap to record')}
      </span>

      {/* Error message */}
      {error && (
        <div className={styles.errorContainer} role="alert">
          <p className={styles.errorMessage}>{error}</p>
          {error.includes('denied') && (
            <p className={styles.errorHint}>
              On iOS: Settings &gt; Safari &gt; Microphone.
              On Android: tap the lock icon in the address bar.
            </p>
          )}
        </div>
      )}

      {/* Reset button when stopped */}
      {isStopped && (
        <button
          className={styles.resetButton}
          onClick={reset}
          type="button"
        >
          Discard &amp; re-record
        </button>
      )}
    </div>
  );
}
